/**
 * Game.ts — Bootstrap e orquestracao do SpaceWar.
 *
 * Fluxo:
 *   1. quemSou() — confirma login no monolito EnsinoSocial
 *   2. Se deslogado, renderiza cartao de login
 *   3. Se logado:
 *      a. Injeta markup do jogo no #app
 *      b. criarToken() → JWT pra abrir WS
 *      c. Conecta WSClient
 *      d. Handlers do WS criam Pixi + UI + input quando partida comeca
 *      e. Estado incremental processa naves/planetas/tiros/eventos
 *
 * Fase 6 (2026-09-27): selecao multipla, envio rapido, relogio, efeitos e
 * fundo com paralaxe. Refator de tres.php:2069-2161 + boot.
 */

import { SpacewarApi, type QuemSouResposta } from '../systems/SpacewarApi';
import { WSClient } from '../ws/Client';
import type {
  MsgPartidaInicio,
  MsgEstado,
  MsgFrotaResposta,
  MsgTorreResposta,
  MsgAuthOk,
  MsgAuthErro,
  EventoDiscreto,
} from '../ws/protocolo';
import { novoEstado, type EstadoJogo } from '../domain/tipos';
import { corPlaneta } from '../config';
import { markupJogo } from '../ui/markup';
import { Splash } from '../ui/Splash';
import { Hud } from '../ui/Hud';
import { Toast } from '../ui/Toast';
import { DialogFrota } from '../ui/DialogFrota';
import { BotaoTorre } from '../ui/BotaoTorre';
import { TelaFim } from '../ui/TelaFim';
import { PixiApp } from '../render/PixiApp';
import { Camera } from '../render/Camera';
import { Estrelas } from '../render/Estrelas';
import { Planetas } from '../render/Planetas';
import { Naves } from '../render/Naves';
import { Tiros } from '../render/Tiros';
import { Efeitos } from '../render/Efeitos';
import { LinhaSelecao } from '../render/LinhaSelecao';
import { Minimapa } from '../render/Minimapa';
import { Selecao } from '../input/Selecao';
import { MouseTouch } from '../input/MouseTouch';
import { Teclado } from '../input/Teclado';
import gsap from 'gsap';

const MOTIVOS_FROTA: Record<string, string> = {
  sem_naves: 'Sem naves para enviar',
  origem_nao_eh_seu: 'Esse planeta nao e seu',
  origem_eq_destino: 'Origem e destino iguais',
  planeta_invalido: 'Planeta invalido',
  sem_origens: 'Selecione um planeta seu primeiro',
  rate_limit: 'Calma: muitas ordens por segundo',
};

export class Game {
  private readonly api = new SpacewarApi();
  private readonly estado: EstadoJogo = novoEstado();

  // Inicializados quando partida_inicio chega
  private pixi: PixiApp | null = null;
  private camera: Camera | null = null;
  private planetas: Planetas | null = null;
  private naves: Naves | null = null;
  private tiros: Tiros | null = null;
  private efeitos: Efeitos | null = null;
  private linhaSelecao: LinhaSelecao | null = null;
  private minimapa: Minimapa | null = null;
  private splash: Splash | null = null;
  private hud: Hud | null = null;
  private toast: Toast | null = null;
  private dialog: DialogFrota | null = null;
  private botaoTorre: BotaoTorre | null = null;
  private telaFim: TelaFim | null = null;
  private selecao: Selecao | null = null;
  private ws: WSClient | null = null;

  constructor(private readonly raiz: HTMLElement) {}

  async iniciar(): Promise<void> {
    let quem: QuemSouResposta;
    try {
      quem = await this.api.quemSou();
    } catch {
      quem = { logado: false };
    }

    if (!quem.logado || !quem.usuario) {
      this.renderLogin();
      return;
    }

    this.iniciarJogo(quem);
  }

  // -------------------------------------------------------------------------
  // Caminho deslogado
  // -------------------------------------------------------------------------

  private renderLogin(): void {
    this.raiz.innerHTML = `
      <div class="cartao-login">
        <div class="cartao-login-inner">
          <h1 class="cartao-titulo">SpaceWar</h1>
          <div class="cartao-subtitulo">login necessario</div>
          <p class="cartao-mensagem">
            Voce precisa estar logado no EnsinoSocial pra jogar.
          </p>
          <div class="cartao-acoes">
            <a href="${this.api.loginUrl}" class="botao botao-primario">Entrar</a>
            <a href="/" class="botao botao-secundario">Voltar ao hub</a>
          </div>
        </div>
      </div>
    `;
  }

  // -------------------------------------------------------------------------
  // Caminho logado
  // -------------------------------------------------------------------------

  private async iniciarJogo(quem: QuemSouResposta): Promise<void> {
    // 1. Injeta markup do splash + jogo
    this.raiz.innerHTML = markupJogo();

    // 2. Inicializa Splash + Toast (so HTML, nao precisa de Pixi)
    this.splash = new Splash();
    this.toast = new Toast();

    // 3. Cria token pra abrir WS
    this.splash.ok('token', 'Validando credenciais...');
    const t = await this.api.criarToken();
    if (!t.ok || !t.token) {
      this.splash.erro('token', `Falha: ${t.motivo || 'desconhecido'}`);
      return;
    }
    this.splash.ok('token', `Credenciais geradas (${t.token.slice(0, 8)}...)`);

    // 4. Conecta WS
    this.splash.mostrar('conexao');
    this.ws = new WSClient({
      onAbrir: (url) => {
        const host = url.split('://')[1].split('?')[0];
        this.splash?.ok('conexao', `Conectado ao servidor (${host})`);
        this.splash?.mostrar('auth');
      },
      onAuthOk: (msg) => this.onAuthOk(msg, quem),
      onAuthErro: (msg) => this.onAuthErro(msg),
      onPartidaInicio: (msg) => this.onPartidaInicio(msg),
      onEstado: (msg) => this.onEstado(msg),
      onFrotaResposta: (msg) => this.onFrotaResposta(msg),
      onTorreResposta: (msg) => this.onTorreResposta(msg),
      onPartidaFim: (msg) => this.telaFim?.mostrar(msg),
      onErro: () => this.splash?.erro('conexao', 'Erro de WebSocket: verifique o servidor'),
      onFechar: (code) => {
        if (!this.estado.salaId) {
          this.splash?.erro('conexao', `Conexao fechada (codigo ${code})`);
        }
      },
    });
    this.ws.conectar(t.token);
  }

  // -------------------------------------------------------------------------
  // Handlers WS
  // -------------------------------------------------------------------------

  private onAuthOk(msg: MsgAuthOk, quem: QuemSouResposta): void {
    this.splash?.ok('auth', `Autenticado como ${msg.usuario.nome || quem.usuario?.nome || ''}`);
    this.estado.meuId = msg.usuario.id;
    this.splash?.mostrar('partida');
  }

  private onAuthErro(msg: MsgAuthErro): void {
    this.splash?.erro('auth', `Falha de auth: ${msg.motivo || 'desconhecido'}`);
  }

  private async onPartidaInicio(msg: MsgPartidaInicio): Promise<void> {
    this.splash?.ok('partida', `Mapa carregado (${msg.planetas.length} planetas)`);

    // Popular estado
    this.estado.salaId = msg.sala_id;
    this.estado.suaCor = msg.sua_cor;
    this.estado.tamanhoMapa = msg.tamanho_mapa || 3000;
    this.estado.config = msg.configs;

    for (const p of msg.planetas) {
      this.estado.planetas.set(p.id, {
        id: p.id,
        x: p.x,
        y: p.y,
        tamanho: p.tamanho,
        raio: p.raio,
        donoId: p.dono_id,
        naves: p.num_naves,
        torres: p.num_torres,
        slotsTorre: p.slots_torre,
        capacidade: p.capacidade,
        especial: p.especial ?? null,
        constelacao: p.constelacao ?? 0,
      });
    }

    // Inicializar Pixi e tudo que depende dele
    await this.inicializarRenderEUi();

    // Naves iniciais
    if (msg.naves) {
      for (const n of msg.naves) this.naves!.adicionarOuAtualizar(n);
    }
    const eu = msg.jogadores?.find((j) => j.slot_cor === msg.sua_cor);
    this.hud!.setNome(eu?.nome || `Comandante ${this.estado.meuId}`);
    this.hud!.atualizarTudo();
    this.hud!.atualizarContadoresNaves();

    // Centraliza no meu planeta
    setTimeout(() => this.centralizarMeuPlaneta(), 100);

    // Fade splash → jogo
    setTimeout(() => {
      this.splash?.esconderEMostrarJogo();
      gsap.from('.sw-hud-card', { y: -20, opacity: 0, duration: 0.6, stagger: 0.1, ease: 'power2.out' });
      gsap.from('.sw-minimapa', { x: 50, opacity: 0, duration: 0.6, delay: 0.3, ease: 'power2.out' });
      gsap.from('.sw-controles', { x: -30, opacity: 0, duration: 0.6, delay: 0.4, ease: 'power2.out' });
      gsap.from('.sw-barra', { y: 30, opacity: 0, duration: 0.6, delay: 0.5, ease: 'power2.out' });
      this.toast?.mostrar(msg.aviso || 'Domine 80% dos planetas.', 5000);
    }, 600);
  }

  private onEstado(msg: MsgEstado): void {
    if (!this.naves || !this.planetas) return;
    this.estado.ticksRecebidos++;
    if (typeof msg.t === 'number') this.estado.tempoDecorrido = msg.t;

    // 1. Naves
    if (msg.naves) {
      for (const n of msg.naves) this.naves.adicionarOuAtualizar(n);
    }
    if (msg.mortas) {
      for (const id of msg.mortas) {
        this.naves.remover(id);
        this.estado.navesMortas++;
      }
    }
    // 2. Planetas
    if (msg.planetas) {
      for (const pData of msg.planetas) {
        const p = this.estado.planetas.get(pData.id);
        if (!p) continue;
        const mudouDono = p.donoId !== pData.dono_id;
        p.donoId = pData.dono_id;
        p.naves = pData.num_naves;
        p.torres = pData.num_torres;
        if (mudouDono || !p.spriteContainer) {
          this.planetas.criarERegistrar(p);
          if (mudouDono && p.donoId !== this.estado.suaCor) this.selecao?.remover(p.id);
        } else {
          this.planetas.atualizarTextoNaves(p);
        }
      }
    }
    // 3. Tiros
    if (msg.tiros) {
      for (const t of msg.tiros) this.tiros?.criar(t);
    }
    // 4. Eventos
    if (msg.eventos) {
      for (const ev of msg.eventos) this.processarEvento(ev);
    }
    // 5. HUD a cada 10 ticks; relogio a cada 20 (1 s)
    if (this.estado.ticksRecebidos % 10 === 0) this.hud?.atualizarContadoresNaves();
    if (this.estado.ticksRecebidos % 20 === 0) this.hud?.atualizarRelogio();
  }

  private processarEvento(ev: EventoDiscreto): void {
    if (ev.evento === 'planeta_capturado') {
      const p = this.estado.planetas.get(ev.planeta_id);
      if (p) this.efeitos?.explosaoCaptura(p.x, p.y, p.raio, corPlaneta(ev.dono_novo_id));
      const meu = ev.dono_novo_id === this.estado.suaCor;
      const perdi = ev.dono_anterior_id === this.estado.suaCor;
      if (meu || perdi) {
        let msgCap = meu ? `P${ev.planeta_id} capturado` : `P${ev.planeta_id} perdido`;
        if (ev.torres_destruidas && ev.torres_destruidas > 0) {
          const plural = ev.torres_destruidas > 1 ? 's' : '';
          msgCap += ` (${ev.torres_destruidas} torre${plural} destruida${plural})`;
        }
        this.toast?.mostrar(msgCap, 2200, meu ? 'bom' : 'ruim');
      }
      if (p?.spriteContainer) {
        gsap.fromTo(
          p.spriteContainer.scale,
          { x: 1.4, y: 1.4 },
          { x: 1, y: 1, duration: 0.5, ease: 'elastic.out(1, 0.5)' },
        );
      }
    } else if (ev.evento === 'torre_construida') {
      const p = this.estado.planetas.get(ev.planeta_id);
      if (p) this.efeitos?.pulsoTorre(p.x, p.y, p.raio, corPlaneta(ev.dono_id));
      if (p?.spriteContainer) {
        gsap.fromTo(
          p.spriteContainer.scale,
          { x: 1.25, y: 1.25 },
          { x: 1, y: 1, duration: 0.45, ease: 'elastic.out(1.2, 0.5)' },
        );
      }
      if (ev.dono_id === this.estado.suaCor) {
        this.toast?.mostrar(`Torre construida em P${ev.planeta_id} (${ev.torres_apos})`);
      }
    }
  }

  private onFrotaResposta(msg: MsgFrotaResposta): void {
    if (!msg.ok) this.toast?.mostrar(MOTIVOS_FROTA[msg.motivo || ''] || `Frota: ${msg.motivo || 'erro'}`, 2200, 'ruim');
  }

  private onTorreResposta(msg: MsgTorreResposta): void {
    if (msg.ok) return;
    let texto = `Torre: ${msg.motivo || 'erro'}`;
    if (msg.motivo === 'naves_insuficientes') {
      texto = `Precisa de ${msg.necessarias} naves orbitando (tem ${msg.disponiveis})`;
    } else if (msg.motivo === 'sem_slot_de_torre') {
      texto = 'Planeta ja tem o maximo de torres';
    }
    this.toast?.mostrar(texto, 2200, 'ruim');
  }

  /**
   * Manda a frota: 1 origem com quantidade exata = frota_lancar_planeta (Fase 4);
   * varias origens (ou envio rapido) = frota_lancar_multi com o percentual (Fase 6).
   */
  private enviarFrota(origens: number[], destinoId: number, pct: number, quantidadeExata?: number): void {
    if (origens.length === 0) return;
    if (origens.length === 1 && quantidadeExata !== undefined) {
      this.ws?.enviar({ tipo: 'frota_lancar_planeta', origem: origens[0], destino: destinoId, quantidade: quantidadeExata });
      return;
    }
    this.ws?.enviar({ tipo: 'frota_lancar_multi', origens, destino: destinoId, pct });
  }

  // -------------------------------------------------------------------------
  // Init dos sistemas de render + ui + input (chamado em partida_inicio)
  // -------------------------------------------------------------------------

  private async inicializarRenderEUi(): Promise<void> {
    const container = document.getElementById('swPixiContainer');
    if (!container) throw new Error('#swPixiContainer nao encontrado');

    this.pixi = new PixiApp();
    await this.pixi.init(container);

    this.camera = new Camera(this.pixi);
    this.camera.tamanhoMapa = this.estado.tamanhoMapa;
    this.camera.iniciar();

    new Estrelas(this.pixi, this.camera).iniciar();

    this.planetas = new Planetas(this.pixi, this.estado);
    this.naves = new Naves(this.pixi, this.estado);
    this.naves.iniciar();
    this.tiros = new Tiros(this.pixi);
    this.tiros.iniciar();
    this.efeitos = new Efeitos(this.pixi);
    this.linhaSelecao = new LinhaSelecao(this.pixi, this.estado, this.camera);

    this.hud = new Hud(this.estado);
    this.dialog = new DialogFrota(this.estado, this.camera, this.pixi);
    this.dialog.onEnviar((envio) => this.enviarFrota(envio.origens, envio.destinoId, envio.pct, envio.quantidade));
    this.dialog.onFechar(() => this.selecao?.cancelar());

    this.selecao = new Selecao(this.estado, this.camera, this.dialog);
    this.selecao.onEnvioRapido((origens, destino) => this.enviarFrota(origens, destino, this.estado.pctEnvio));
    this.selecao.onMudar(() => { if (this.selecao && this.selecao.quantidade === 0) this.linhaSelecao?.limpar(); });
    this.linhaSelecao.iniciar(() => this.selecao?.ids ?? []);

    this.botaoTorre = new BotaoTorre(this.estado, this.camera, this.pixi);
    this.botaoTorre.conectar(
      () => this.selecao?.origemId ?? null,
      (planetaId) => this.ws?.enviar({ tipo: 'torre_construir', planeta: planetaId }),
    );
    // Atualizar botao torre todo frame
    this.pixi.addTicker(() => {
      this.botaoTorre?.atualizar(this.dialog?.visivel() ?? false);
    });

    this.telaFim = new TelaFim(this.estado);

    new MouseTouch(this.pixi.canvas, this.camera, this.selecao, this.linhaSelecao, this.estado).iniciar();
    new Teclado(this.estado, this.camera, this.selecao, this.dialog, () => this.hud?.atualizarPct()).iniciar();

    // Barra de toque: chips de percentual, Todos, Varios
    document.querySelectorAll<HTMLButtonElement>('.sw-chip[data-pct]').forEach((chip) => {
      chip.addEventListener('click', () => {
        this.estado.pctEnvio = parseFloat(chip.dataset.pct || '0.5');
        this.hud?.atualizarPct();
      });
    });
    document.getElementById('swBtnTodos')?.addEventListener('click', () => this.selecao?.selecionarTodos());
    document.getElementById('swBtnMulti')?.addEventListener('click', () => {
      this.estado.modoMulti = !this.estado.modoMulti;
      this.hud?.atualizarPct();
    });

    // Minimapa
    const canvas = document.getElementById('swMinimapaCanvas') as HTMLCanvasElement;
    const minimapaEl = document.getElementById('swMinimapa');
    if (canvas && minimapaEl) {
      this.minimapa = new Minimapa(this.pixi, this.camera, this.estado);
      this.minimapa.iniciar(canvas, minimapaEl);
    }

    // Render inicial dos planetas
    this.planetas.renderTodos();
  }

  private centralizarMeuPlaneta(): void {
    if (!this.camera) return;
    for (const p of this.estado.planetas.values()) {
      if (p.donoId === this.estado.suaCor) {
        this.camera.setAlvo(p.x, p.y, 1.0);
        return;
      }
    }
  }
}
